<template>
  <div class="album-edit-form">
    <!-- Edit mode indicator -->
    <div class="edit-mode-indicator">
      <div class="edit-badge">
        <span class="edit-icon">✏️</span>
        <span class="edit-text">Edit Mode</span>
      </div>
      <div class="dirty-indicator" v-if="isDirty" role="status" aria-live="polite">
        <span class="dirty-icon">●</span>
        <span class="dirty-text">Unsaved changes</span>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="loading-spinner"></div>
      <span>Loading album data...</span>
    </div>
    
    <div v-else-if="error" class="error-state" role="alert">
      <span class="error-icon">⚠️</span>
      {{ error }}
    </div>
    
    <!-- Authentication Check - Show modal if not authenticated -->
    <div v-else-if="!authStore.isAuthValid" class="auth-step">
      <div class="form-section auth-section">
        <div class="section-header">
          <h2 class="section-title">🔐 Authentication Required</h2>
          <p class="section-description">Please authenticate to edit this album</p>
        </div>

        <div class="form-actions" style="justify-content:flex-start; margin-top: 1rem;">
          <button type="button" class="save-button" @click="handleAuthRequired">Authenticate</button>
        </div>
      </div>
    </div>
    
    <!-- Edit Form (only shown after authentication) -->
    <form v-else @submit.prevent="updateAlbum" class="edit-form">
      <!-- Basic Information Section -->
      <div class="form-section">
        <div class="section-header">
          <h2 class="section-title">🎵 Basic Information</h2>
          <p class="section-description">Edit core album details</p>
        </div>
        
        <div class="form-row">
          <div class="form-col">
            <EnhancedFormField
              v-model="form.artist"
              label="Artist"
              type="text"
              placeholder="Enter artist name"
              :required="true"
              :error="getFieldError('artist')"
              :loading="validationLoading.artist"
              @validate="handleFieldValidation('artist', $event)"
              @input="markDirty"
            />
          </div>
          <div class="form-col">
            <EnhancedFormField
              v-model="form.title"
              label="Album Title"
              type="text"
              placeholder="Enter album title"
              :required="true"
              :error="getFieldError('title')"
              :loading="validationLoading.title"
              @validate="handleFieldValidation('title', $event)"
              @input="markDirty"
            />
          </div>
        </div>
        
        <EnhancedFormField
          v-model="form.cover_url"
          label="Album Cover URL"
          type="url"
          placeholder="https://example.com/album-cover.jpg"
          :required="true"
          :error="getFieldError('cover_url')"
          :loading="validationLoading.cover_url"
          help-text="URL for the album cover image"
          @validate="handleFieldValidation('cover_url', $event)"
          @input="markDirty"
        />
      </div>

      <!-- Spotify Information Section -->
      <div class="form-section">
        <div class="section-header">
          <h2 class="section-title">🎧 Spotify Information</h2>
          <p class="section-description">Spotify integration details</p>
        </div>
        
        <div class="form-row">
          <div class="form-col">
            <EnhancedFormField
              v-model="form.spotify_id"
              label="Spotify ID"
              type="text"
              placeholder="e.g., 4aawyAB9vmqN3uQ7FjRGTy"
              :required="true"
              :error="getFieldError('spotify_id')"
              help-text="22-character Spotify album identifier"
              @validate="handleFieldValidation('spotify_id', $event)"
              @input="markDirty"
            />
          </div>
        </div>
        
        <EnhancedFormField
          v-model="form.spotify_url"
          label="Spotify URL"
          type="url"
          placeholder="https://open.spotify.com/album/..."
          :required="true"
          :error="getFieldError('spotify_url')"
          help-text="Full Spotify album URL"
          @validate="handleFieldValidation('spotify_url', $event)"
          @input="markDirty"
        />

        <div class="form-row">
          <div class="form-col">
            <EnhancedFormField
              v-model.number="form.release_year"
              label="Release Year"
              type="number"
              placeholder="e.g. 1994"
              :min="1900"
              :max="new Date().getFullYear()"
              help-text="Leave blank if unknown"
              @input="markDirty"
            />
          </div>
          <div class="form-col">
            <EnhancedFormField
              v-model="form.release_month"
              label="Release Month"
              type="select"
              :options="monthOptions"
              help-text="Leave blank if unknown"
              @change="markDirty"
            />
          </div>
        </div>
      </div>

      <!-- Tidal Information Section -->
      <div class="form-section tidal-section">
        <div class="section-header">
          <h2 class="section-title">🌊 Tidal Information</h2>
          <p class="section-description">Tidal streaming service integration (optional)</p>
        </div>
        
        <TidalIdField
          v-model="form.tidal_album_id"
          :auto-populated="false"
          :can-override="true"
          :error="getFieldError('tidal_album_id')"
          @validate="handleFieldValidation('tidal_album_id', $event)"
          @input="markDirty"
        />
      </div>

      <!-- Genres Section -->
      <div class="form-section">
        <div class="section-header">
          <h2 class="section-title">🎵 Genres</h2>
          <p class="section-description">Up to 3 genres from the controlled list</p>
        </div>
        <GenrePicker
          v-model="genreValue"
          @update:modelValue="markDirty"
        />
      </div>

      <!-- Review Information Section -->
      <div class="form-section">
        <div class="section-header">
          <h2 class="section-title">📊 Review Information</h2>
          <p class="section-description">Session and rating details</p>
        </div>

        <div class="form-row">
          <div class="form-col">
            <EnhancedFormField
              v-model="form.session_id"
              label="Session"
              type="select"
              placeholder="Select a session"
              :required="true"
              :options="sessionOptions"
              :error="getFieldError('session_id')"
              @validate="handleFieldValidation('session_id', $event)"
              @change="markDirty"
            />
          </div>
          <div class="form-col">
            <EnhancedFormField
              v-model="form.submitted_by"
              label="Submitted By"
              type="select"
              placeholder="Select submitter"
              :required="true"
              :options="submitterOptions"
              :error="getFieldError('submitted_by')"
              @validate="handleFieldValidation('submitted_by', $event)"
              @change="markDirty"
            />
          </div>
        </div>
      </div>

      <!-- Individual Ratings Section -->
      <div class="form-section" v-if="form.individual_tiers">
        <div class="section-header">
          <h2 class="section-title">👥 Individual Ratings</h2>
          <p class="section-description">Tier ratings from each reviewer</p>
        </div>
        
        <div class="tier-ratings-grid">
          <div 
            v-for="(tier, reviewer) in form.individual_tiers" 
            :key="reviewer" 
            class="reviewer-tier-section"
          >
            <label class="reviewer-label">{{ reviewer }}'s Rating</label>
            <div class="tier-buttons">
              <button
                type="button"
                v-for="option in tiersList"
                :key="option"
                :class="['tier-button', { active: form.individual_tiers[reviewer] === option }]"
                @click="updateIndividualTier(reviewer, option)"
              >
                {{ option }}
              </button>
            </div>
          </div>
        </div>
        
        <div class="score-calculation">
          <button type="button" @click="calculateScore" class="score-button" :disabled="calculatingScore">
            <span v-if="calculatingScore" class="button-spinner"></span>
            {{ calculatingScore ? 'Calculating...' : 'Calculate Score' }}
          </button>
          
          <div v-if="scoreResult" class="score-result">
            <p><strong>Average Score:</strong> {{ scoreResult.averageScore ?? 'N/A' }}</p>
            <p><strong>Suggested Tier:</strong> {{ scoreResult.suggestedTier ?? 'No suggestion' }}</p>
          </div>
        </div>
      </div>

      <!-- Overall Tier Section -->
      <div class="form-section">
        <div class="section-header">
          <h2 class="section-title">🏆 Overall Tier</h2>
          <p class="section-description">Final tier assignment for this album</p>
        </div>

        <div class="tier-buttons">
          <button
            type="button"
            v-for="option in tiersList"
            :key="option"
            :class="['tier-button', { active: form.tier === option }]"
            @click="updateOverallTier(option)"
          >
            {{ option }}
          </button>
        </div>
      </div>

      <!-- Special Options Section -->
      <div class="form-section">
        <div class="section-header">
          <h2 class="section-title">⚙️ Special Options</h2>
          <p class="section-description">Additional album settings</p>
        </div>
        
        <EnhancedFormField
          v-model="form.veto"
          label=""
          type="checkbox"
          checkbox-label="This album has been vetoed"
          :error="getFieldError('veto')"
          help-text="Check if this album was rejected during review"
          @validate="handleFieldValidation('veto', $event)"
          @change="markDirty"
        />
        
        <div v-if="form.veto" class="veto-details">
          <EnhancedFormField
            v-model="form.veto_by"
            label="Vetoed By"
            type="text"
            placeholder="Name of person who vetoed"
            :error="getFieldError('veto_by')"
            @validate="handleFieldValidation('veto_by', $event)"
            @input="markDirty"
          />
        </div>
      </div>

      <!-- Form Actions -->
      <div class="form-actions">
        <button 
          type="button" 
          class="cancel-button"
          @click="handleCancel"
          :disabled="submitLoading"
        >
          Cancel
        </button>
        <button 
          type="submit" 
          class="save-button"
          :disabled="!canSave"
          :class="{ loading: submitLoading }"
        >
          <span v-if="submitLoading" class="button-spinner"></span>
          {{ submitLoading ? 'Saving Changes...' : 'Save Changes' }}
        </button>
      </div>
      
      <!-- Status Messages -->
      <div v-if="success" class="status-message success-message" role="status">
        ✅ {{ success }}
      </div>
      <div v-if="submitError" class="status-message error-message" role="alert">
        ⚠️ {{ submitError }}
      </div>
    </form>

    <!-- Unsaved Changes Modal -->
    <div v-if="showUnsavedModal" class="modal-overlay" @click="closeUnsavedModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Unsaved Changes</h3>
        </div>
        <div class="modal-body">
          <p>You have unsaved changes. Are you sure you want to leave without saving?</p>
        </div>
        <div class="modal-actions">
          <button class="secondary-button" @click="closeUnsavedModal">Stay & Save</button>
          <button class="danger-button" @click="confirmLeave">Discard Changes</button>
        </div>
      </div>
    </div>

    <!-- Admin Authentication Modal -->
    <AdminAuthModal
      :visible="showAuthModal"
      @authenticated="onAuthenticated"
      @close="showAuthModal = false"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch, onBeforeUnmount } from 'vue'
import { useRouter, onBeforeRouteLeave } from 'vue-router'
import EnhancedFormField from './EnhancedFormField.vue'
import TidalIdField from './TidalIdField.vue'
import AdminAuthModal from '../modals/AdminAuthModal.vue'
import GenrePicker from './GenrePicker.vue'
import { useFormValidation, albumValidationSchema } from '../../composables/useFormValidation.js'
import { useAuthStore } from '../../stores/auth.js'
import { useViewStatePreservation } from '../../composables/useViewStatePreservation.js'
import apiService from '../../apiService.js'
import scoreService from '../../services/scoreService.js'

export default {
  name: 'AlbumEditForm',
  components: {
    EnhancedFormField,
    TidalIdField,
    AdminAuthModal,
    GenrePicker
  },
  props: {
    id: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const router = useRouter()
    const authStore = useAuthStore()
    const { restoreViewState } = useViewStatePreservation()

    // Form validation
    const {
      validateForm,
      hasError,
      getFieldError,
      validateField,
      clearFieldError
    } = useFormValidation()
    
    // Form state
    const form = ref({
      artist: '',
      title: '',
      submitted_by: '',
      session_id: '',
      tier: '',
      spotify_id: '',
      spotify_url: '',
      tidal_album_id: '',
      cover_url: '',
      veto: false,
      veto_by: '',
      individual_tiers: {},
      mainGenres: [],
      subGenres: [],
    })
    
    const originalForm = ref({})
    const showAuthModal = ref(false)
    
    // UI state
    const loading = ref(true)
    const error = ref('')
    const submitLoading = ref(false)
    const submitError = ref('')
    const success = ref('')
    const isDirty = ref(false)
    const showUnsavedModal = ref(false)
    const calculatingScore = ref(false)
    const scoreResult = ref(null)
    const sessions = ref([])
    const pendingNavigation = ref(null)
    
    // Validation loading states
    const validationLoading = ref({
      artist: false,
      title: false,
      cover_url: false
    })
    
    // Options for select fields
    const submitterOptions = computed(() => [
      { value: 'Arno', label: 'Arno' },
      { value: 'Tyrone', label: 'Tyrone' },
      { value: 'Lucas', label: 'Lucas' },
      { value: 'Thomas', label: 'Thomas' },
      { value: 'Wouter', label: 'Wouter' }
    ])
    
    const monthOptions = [
      { value: null, label: 'Unknown' },
      { value: 1, label: 'January' }, { value: 2, label: 'February' },
      { value: 3, label: 'March' }, { value: 4, label: 'April' },
      { value: 5, label: 'May' }, { value: 6, label: 'June' },
      { value: 7, label: 'July' }, { value: 8, label: 'August' },
      { value: 9, label: 'September' }, { value: 10, label: 'October' },
      { value: 11, label: 'November' }, { value: 12, label: 'December' }
    ]

    const tierOptions = computed(() => [
      { value: 'S', label: '🏆 S Tier' },
      { value: 'A', label: '🥇 A Tier' },
      { value: 'B', label: '🥈 B Tier' },
      { value: 'C', label: '🥉 C Tier' },
      { value: 'D', label: '📀 D Tier' },
      { value: 'E', label: '💿 E Tier' },
      { value: 'F', label: '🔻 F Tier' },
      { value: 'Tiering Herrie', label: '🗑️ Tiering Herrie' },
      { value: '?', label: '❓ Unknown' },
      { value: 'Now Listening To', label: '🎵 Now Listening To' },
      { value: 'Not rated yet', label: '❓ Not rated yet' }
    ])
    
    const tiersList = ["S", "A", "B", "C", "D", "E", "F", "Tiering Herrie", "?", "Now Listening To", "Not rated yet"]
    
    const sessionOptions = computed(() => 
      sessions.value.map(session => ({
        value: session.id,
        label: session.label
      }))
    )
    
    // Form validation
    const canSave = computed(() => {
      const validationData = { ...form.value }
      // Create a validation schema that excludes the password field since it's handled separately
      const editFormValidationSchema = { ...albumValidationSchema }
      delete editFormValidationSchema.password

      const isValid = validateForm(validationData, editFormValidationSchema)
      // Allow saving if authenticated and form is valid
      // The isDirty check is handled in the updateAlbum function
      return isValid && authStore.isAuthValid && !submitLoading.value
    })
    
    // Methods
    const handleAuthRequired = () => {
      if (!authStore.isAuthValid) {
        showAuthModal.value = true
      }
    }

    const onAuthenticated = () => {
      showAuthModal.value = false
      // Auth state now valid, form will become editable
    }
    const markDirty = () => {
      if (!isDirty.value) {
        isDirty.value = true
      }
    }
    
    const handleFieldValidation = (field, validationResult) => {
      // Handle field-specific validation if needed
      if (validationLoading.value[field]) {
        validationLoading.value[field] = false
      }
      
      // If validationResult is provided, use it to update the validation state
      if (validationResult) {
        if (!validationResult.isValid) {
          // Field has an error - the error is already set in the validation system
          // No need to manually set it here as validateField already does this
        } else {
          // Field is valid, clear any existing error
          clearFieldError(field)
        }
      } else {
        // If no validationResult provided, validate the field manually
        const value = form.value[field]
        
        const fieldRules = albumValidationSchema[field]?.rules || []
        const result = validateField(field, value, fieldRules, albumValidationSchema[field]?.displayName)
        
        if (!result.isValid) {
          // Error is already set by validateField
        } else {
          clearFieldError(field)
        }
      }
    }
    
    const updateIndividualTier = (reviewer, tier) => {
      form.value.individual_tiers[reviewer] = tier
      markDirty()
    }

    const updateOverallTier = (tier) => {
      form.value.tier = tier
      markDirty()
    }
    
    const calculateScore = async () => {
      calculatingScore.value = true
      try {
        const tiers = Object.values(form.value.individual_tiers)
        scoreResult.value = await scoreService.calculateScore(tiers)
      } catch (err) {
        console.error('Score calculation error:', err)
        submitError.value = 'Failed to calculate score. Please try again.'
      } finally {
        calculatingScore.value = false
      }
    }
    
    const loadAlbumData = async () => {
      loading.value = true
      error.value = ''
      
      try {
        const [albumResponse, sessionsResponse] = await Promise.all([
          apiService.getAlbum(props.id),
          apiService.WorkspaceSessions()
        ])
        
        // Set form data
        form.value = {
          ...albumResponse,
          mainGenres: albumResponse.mainGenres || [],
          subGenres:  albumResponse.subGenres  || [],
        }
        originalForm.value = {
          ...albumResponse,
          mainGenres: albumResponse.mainGenres || [],
          subGenres:  albumResponse.subGenres  || [],
        }
        
        // Set sessions with formatted dates
        sessions.value = (Array.isArray(sessionsResponse.data) ? sessionsResponse.data : sessionsResponse).map(s => ({
          ...s,
          label: s.session_date 
            ? `${new Date(s.session_date).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })} - ${s.location || 'No location'}`
            : s.location || `Session ${s.id}`
        }))
        
        loading.value = false
      } catch (err) {
        console.error('Error loading album data:', err)
        error.value = 'Failed to load album data. Please try again.'
        loading.value = false
      }
    }
    
    const updateAlbum = async () => {
      // Create a validation schema that excludes the password field since it's handled separately
      const editFormValidationSchema = { ...albumValidationSchema }
      delete editFormValidationSchema.password

      const isValid = validateForm(form.value, editFormValidationSchema)
      if (!isValid || !authStore.isAuthValid) {
        submitError.value = !authStore.isAuthValid ? 'Please authenticate with admin password first' : 'Please fix the errors above'
        return
      }

      submitLoading.value = true
      success.value = ''
      submitError.value = ''
      try {
        // Get admin password from auth store for authentication
        const adminPassword = authStore.adminPassword
        await apiService.updateAlbum(props.id, { ...form.value }, adminPassword)
        await apiService.updateAlbumGenres(
          parseInt(props.id),
          { mainGenres: form.value.mainGenres, subGenres: form.value.subGenres },
          adminPassword
        )
        success.value = 'Album updated successfully!'
        isDirty.value = false
        originalForm.value = { ...form.value }

        // Determine where to navigate after save
        setTimeout(() => {
          const savedState = restoreViewState()

          if (savedState && savedState.viewMode === 'tierListView') {
            // Return to tierlist if that's where the user came from
            router.push('/tierlist')
          } else if (savedState && savedState.viewMode === 'sessionView') {
            // Return to sessions if that's where the user came from
            router.push('/')
          } else {
            // Default fallback to sessions homepage
            router.push('/')
          }
        }, 2000)
      } catch (err) {
        submitError.value = err.message || 'Failed to update album. Please try again.'
      } finally {
        submitLoading.value = false
      }
    }
    
    const handleCancel = () => {
      if (isDirty.value) {
        showUnsavedModal.value = true
      } else {
        // Navigate to the appropriate view based on saved state
        const savedState = restoreViewState()
        if (savedState && savedState.viewMode === 'tierListView') {
          router.push('/tierlist')
        } else if (savedState && savedState.viewMode === 'sessionView') {
          router.push('/')
        } else {
          router.push('/')
        }
      }
    }
    
    const closeUnsavedModal = () => {
      showUnsavedModal.value = false
      pendingNavigation.value = null
    }
    
    const confirmLeave = () => {
      isDirty.value = false
      showUnsavedModal.value = false

      if (pendingNavigation.value) {
        pendingNavigation.value()
      } else {
        // Navigate to the appropriate view based on saved state
        const savedState = restoreViewState()
        if (savedState && savedState.viewMode === 'tierListView') {
          router.push('/tierlist')
        } else if (savedState && savedState.viewMode === 'sessionView') {
          router.push('/')
        } else {
          router.push('/')
        }
      }
    }
    
    // Watch for form changes to detect dirty state
    watch(form, () => {
      if (!loading.value) {
        const hasChanges = JSON.stringify(form.value) !== JSON.stringify(originalForm.value)
        if (hasChanges && !isDirty.value) {
          markDirty()
        }
      }
    }, { deep: true })
    
    // Navigation guard for unsaved changes
    onBeforeRouteLeave((to, from, next) => {
      if (isDirty.value) {
        showUnsavedModal.value = true
        pendingNavigation.value = next
      } else {
        next()
      }
    })
    
    // Browser unload protection
    const beforeUnloadHandler = (event) => {
      if (isDirty.value) {
        event.preventDefault()
        event.returnValue = ''
      }
    }
    
    onMounted(() => {
      loadAlbumData()
      window.addEventListener('beforeunload', beforeUnloadHandler)
    })
    
    onBeforeUnmount(() => {
      window.removeEventListener('beforeunload', beforeUnloadHandler)
    })
    
    const genreValue = computed({
      get: () => ({ main: form.value.mainGenres, sub: form.value.subGenres }),
      set: (v) => { form.value.mainGenres = v.main; form.value.subGenres = v.sub; },
    });

    return {
      form,
      genreValue,
      authStore,
      showAuthModal,
      loading,
      error,
      submitLoading,
      submitError,
      success,
      isDirty,
      showUnsavedModal,
      calculatingScore,
      scoreResult,
      validationLoading,
      submitterOptions,
      monthOptions,
      tierOptions,
      tiersList,
      sessionOptions,
      canSave,
      markDirty,
      handleFieldValidation,
      updateIndividualTier,
      updateOverallTier,
      calculateScore,
      updateAlbum,
      handleAuthRequired,
      onAuthenticated,
      handleCancel,
      closeUnsavedModal,
      confirmLeave,
      hasError,
      getFieldError
    }
  }
}
</script>

<style scoped>
/* Import the tier colors */
@import '../../styles/tier-colors.css';

.album-edit-form {
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem;
  background: linear-gradient(135deg, #2a2a2a 0%, #1e1e1e 100%);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  color: #f3f3f3;
  position: relative;
}

/* Edit mode indicator */
.edit-mode-indicator {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1rem;
  background: rgba(0, 191, 255, 0.1);
  border: 1px solid rgba(0, 191, 255, 0.3);
  border-radius: 8px;
}

.edit-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--tier-d-color);
  font-weight: 600;
}

.edit-icon {
  font-size: 1.25rem;
}

.dirty-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--tier-s-color);
  font-size: 0.875rem;
  font-weight: 500;
}

.dirty-icon {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* Loading and error states */
.loading-state,
.error-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 3rem;
  text-align: center;
}

.loading-spinner {
  width: 2rem;
  height: 2rem;
  border: 3px solid #444;
  border-top: 3px solid var(--tier-d-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.error-state {
  color: var(--tier-s-color);
  font-size: 1.125rem;
}

.error-icon {
  font-size: 1.5rem;
}

/* Form sections */
.form-section {
  margin-bottom: 2.5rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.auth-section {
  background: rgba(255, 69, 0, 0.03);
  border-color: rgba(255, 69, 0, 0.1);
}

.tidal-section {
  background: rgba(0, 191, 255, 0.02);
  border-color: rgba(0, 191, 255, 0.1);
}

.section-header {
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #f3f3f3;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.section-description {
  font-size: 0.875rem;
  color: #aaa;
  margin: 0;
}

/* Form layout */
.form-row {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1rem;
}

.form-col {
  flex: 1;
}

/* Individual ratings */
.tier-ratings-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.reviewer-tier-section {
  padding: 1rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.reviewer-label {
  display: block;
  font-weight: 600;
  color: #f3f3f3;
  margin-bottom: 0.75rem;
  font-size: 0.875rem;
}

.tier-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.tier-button {
  padding: 0.5rem 0.75rem;
  border: none;
  border-radius: 6px;
  background-color: #333;
  color: #f3f3f3;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 500;
  transition: var(--tier-transition-fast);
  min-width: 2.5rem;
}

.tier-button:hover {
  background-color: #444;
}

.tier-button.active {
  background-color: var(--tier-d-color);
  color: white;
  font-weight: 600;
}

/* Score calculation */
.score-calculation {
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.score-button {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  background-color: var(--tier-c-color);
  color: white;
  cursor: pointer;
  font-weight: 600;
  transition: var(--tier-transition-fast);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.score-button:hover:not(:disabled) {
  background-color: var(--tier-c-light);
  transform: translateY(-1px);
}

.score-button:disabled {
  background-color: #666;
  cursor: not-allowed;
  transform: none;
}

.score-result {
  margin-top: 1rem;
  padding: 1rem;
  background: rgba(50, 205, 50, 0.1);
  border: 1px solid rgba(50, 205, 50, 0.2);
  border-radius: 8px;
  color: var(--tier-c-color);
}

.score-result p {
  margin: 0.5rem 0;
}

/* Special sections */
.veto-details {
  margin-top: 1rem;
  padding: 1rem;
  background: rgba(255, 69, 0, 0.05);
  border: 1px solid rgba(255, 69, 0, 0.2);
  border-radius: 8px;
}

/* Form actions */
.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  align-items: center;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #404040;
}

/* Buttons */
.save-button,
.cancel-button {
  padding: 0.875rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--tier-transition-fast);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 44px;
  border: none;
}

.save-button {
  background: var(--tier-d-color);
  color: white;
}

.save-button:hover:not(:disabled) {
  background: var(--tier-d-light);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 191, 255, 0.3);
}

.save-button:disabled {
  background: #666;
  color: #999;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.cancel-button {
  background: transparent;
  color: #f3f3f3;
  border: 1px solid #666;
}

.cancel-button:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.05);
  border-color: #888;
}

.button-spinner {
  width: 1rem;
  height: 1rem;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Status messages */
.status-message {
  padding: 1rem;
  border-radius: 8px;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
}

.success-message {
  background: rgba(50, 205, 50, 0.1);
  border: 1px solid rgba(50, 205, 50, 0.3);
  color: var(--tier-c-color);
}

.error-message {
  background: rgba(255, 69, 0, 0.1);
  border: 1px solid rgba(255, 69, 0, 0.3);
  color: var(--tier-s-color);
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: #2a2a2a;
  border-radius: 12px;
  padding: 2rem;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.modal-header h3 {
  margin: 0 0 1rem 0;
  color: #f3f3f3;
  font-size: 1.25rem;
}

.modal-body p {
  margin: 0 0 1.5rem 0;
  color: #ccc;
  line-height: 1.5;
}

.modal-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}

.secondary-button,
.danger-button {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--tier-transition-fast);
  border: none;
}

.secondary-button {
  background: transparent;
  color: #f3f3f3;
  border: 1px solid #666;
}

.secondary-button:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: #888;
}

.danger-button {
  background: var(--tier-s-color);
  color: white;
}

.danger-button:hover {
  background: var(--tier-s-light);
  transform: translateY(-1px);
}

/* Responsive design */
@media (max-width: 768px) {
  .album-edit-form {
    padding: 1rem;
    margin: 1rem;
  }
  
  .edit-mode-indicator {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }
  
  .form-row {
    flex-direction: column;
    gap: 0;
  }
  
  .tier-ratings-grid {
    grid-template-columns: 1fr;
  }
  
  .tier-buttons {
    gap: 0.25rem;
  }
  
  .tier-button {
    flex: 1;
    min-width: auto;
    font-size: 0.625rem;
    padding: 0.5rem 0.25rem;
  }
  
  .form-actions {
    flex-direction: column-reverse;
    gap: 1rem;
  }
  
  .save-button,
  .cancel-button {
    width: 100%;
    justify-content: center;
  }
  
  .modal-content {
    margin: 1rem;
    padding: 1.5rem;
  }
  
  .modal-actions {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .secondary-button,
  .danger-button {
    width: 100%;
    justify-content: center;
  }
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .form-section {
    border-width: 2px;
  }
  
  .save-button,
  .cancel-button,
  .tier-button {
    border-width: 2px;
  }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .edit-mode-indicator,
  .save-button,
  .cancel-button,
  .tier-button,
  .score-button,
  .loading-spinner,
  .button-spinner {
    transition: none;
    animation: none;
  }
  
  .save-button:hover:not(:disabled),
  .score-button:hover:not(:disabled),
  .danger-button:hover {
    transform: none;
  }
  
  .dirty-icon {
    animation: none;
  }
}
</style>